<template>
  <div v-if="!disabled" class="upload-file">
    <template v-if="useMaterialLibrary">
      <el-button type="primary" @click="materialVisible = true">
        <Icon icon="ep:folder-opened" />
        从素材库选择
      </el-button>
      <div v-if="isShowTip" style="font-size: 8px; margin-top: 4px">
        选用已有不耗费 OSS；新上传会计入已用。大小不超过
        <b style="color: #f56c6c">{{ fileSize }}MB</b>
      </div>
      <div
        v-for="(file, index) in fileList"
        :key="index"
        class="flex items-center file-list-item mt-8px"
      >
        <span>{{ file.name }}</span>
        <div class="ml-10px">
          <el-link :href="file.url" :underline="false" download target="_blank" type="primary">
            下载
          </el-link>
        </div>
        <div class="ml-10px">
          <el-button link type="danger" @click="handleRemove(file as any)"> 删除</el-button>
        </div>
      </div>
      <TenantMaterialSelector
        v-model="materialVisible"
        :default-module="directory || ''"
        :file-size-mb="fileSize"
        :media-type="materialMediaType"
        :multiple="limit > 1"
        @confirm="onMaterialConfirm"
      />
    </template>
    <el-upload
      v-else
      ref="uploadRef"
      v-model:file-list="fileList"
      :action="uploadUrl"
      :auto-upload="autoUpload"
      :before-upload="beforeUpload"
      :disabled="disabled"
      :drag="drag"
      :http-request="httpRequest"
      :limit="props.limit"
      :multiple="props.limit > 1"
      :on-error="excelUploadError"
      :on-exceed="handleExceed"
      :on-preview="handlePreview"
      :on-remove="handleRemove"
      :on-success="handleFileSuccess"
      :show-file-list="true"
      class="upload-file-uploader"
      name="file"
    >
      <el-button type="primary">
        <Icon icon="ep:upload-filled" />
        选取文件
      </el-button>
      <template v-if="isShowTip" #tip>
        <div style="font-size: 8px">
          大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>
        </div>
        <div style="font-size: 8px">
          格式为 <b style="color: #f56c6c">{{ fileType.join('/') }}</b> 的文件
        </div>
      </template>
      <template #file="row">
        <div class="file-list-row">
          <el-tooltip :content="row.file.name" placement="top">
            <span class="file-list-name">{{ row.file.name }}</span>
          </el-tooltip>
          <div class="file-list-action ml-10px">
            <el-link
              :href="row.file.url"
              :underline="false"
              download
              target="_blank"
              type="primary"
            >
              下载
            </el-link>
          </div>
          <div class="file-list-action ml-10px">
            <el-button link type="danger" @click="handleRemove(row.file)"> 删除</el-button>
          </div>
        </div>
      </template>
    </el-upload>
  </div>

  <!-- 上传操作禁用时 -->
  <div v-if="disabled" class="upload-file">
    <div v-for="(file, index) in fileList" :key="index" class="flex items-center file-list-item">
      <span>{{ file.name }}</span>
      <div class="ml-10px">
        <el-link :href="file.url" :underline="false" download target="_blank" type="primary">
          下载
        </el-link>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import type { PropType } from 'vue'
import { propTypes } from '@/utils/propTypes'
import { genFileId } from 'element-plus'
import type { UploadInstance, UploadProps, UploadRawFile, UploadUserFile } from 'element-plus'
import { isString } from '@/utils/is'
import { useUpload } from '@/components/UploadFile/src/useUpload'
import { UploadFile } from 'element-plus/es/components/upload/src/upload'
import TenantMaterialSelector from '@/components/TenantMaterialSelector/index.vue'
import type { FileVO } from '@/api/infra/file'

defineOptions({ name: 'UploadFile' })

const message = useMessage() // 消息弹窗
const emit = defineEmits(['update:modelValue', 'upload-success'])

const props = defineProps({
  modelValue: propTypes.oneOfType<string | string[]>([String, Array<String>]).isRequired,
  fileType: propTypes.array.def(['doc', 'xls', 'ppt', 'txt', 'pdf']), // 文件类型, 例如['png', 'jpg', 'jpeg']
  fileSize: propTypes.number.def(5), // 大小限制(MB)
  limit: propTypes.number.def(5), // 数量限制
  autoUpload: propTypes.bool.def(true), // 自动上传
  drag: propTypes.bool.def(false), // 拖拽上传
  isShowTip: propTypes.bool.def(true), // 是否显示提示
  disabled: propTypes.bool.def(false), // 是否禁用上传组件 ==> 非必传（默认为 false）
  directory: propTypes.string.def(undefined), // 上传目录 / 默认模块
  replaceOnExceed: propTypes.bool.def(false), // 单文件超限时自动替换原文件
  materialMediaType: {
    type: String as PropType<'IMAGE' | 'VIDEO' | 'FILE' | ''>,
    default: 'FILE'
  },
  useMaterialLibrary: propTypes.bool.def(true)
})

// ========== 上传相关 ==========
const uploadList = ref<UploadUserFile[]>([])
const fileList = ref<UploadUserFile[]>([])
const uploadNumber = ref<number>(0)
const uploadRef = ref<UploadInstance>()
const materialVisible = ref(false)

const { uploadUrl, httpRequest } = useUpload(props.directory)

/**
 * 从 URL 末段推导文件名（URL 里存的是编码后的文件名，必须 decode）
 *
 * <p>后端 `/infra/file/upload` 只返回一个 URL 字符串，不回原文件名，
 * 因此「编辑回填」「刷新后回显」这类拿不到 `File` 对象的场景只能从 URL 反推。
 */
const fileNameFromUrl = (url?: string): string => {
  if (!url) return ''
  const raw = url.substring(url.lastIndexOf('/') + 1)
  // URL 末段已是编码态，去掉 query/hash 再解码；解码失败（畸形 % 序列）则退回原串
  const withoutQuery = raw.split('?')[0].split('#')[0]
  try {
    return decodeURIComponent(withoutQuery)
  } catch {
    return withoutQuery
  }
}

/**
 * 决定列表里展示的文件名：优先用户本地原始文件名，其次从 URL 反推
 *
 * <p>⚠️ 不要直接用 URL 当显示名 —— 中文名会被编码成 `%E4%BA%91...`，用户看到的是一串乱码。
 */
const resolveFileName = (originalName: string | undefined, url?: string): string =>
  originalName || fileNameFromUrl(url)

const onMaterialConfirm = (items: FileVO[]) => {
  const mapped = items.map((i) => ({
    name: i.name || String(i.id),
    url: i.url
  }))
  if (props.limit === 1) {
    fileList.value = mapped.slice(0, 1)
  } else {
    const remain = props.limit - fileList.value.length
    fileList.value = [...fileList.value, ...mapped.slice(0, Math.max(0, remain))]
  }
  emitUpdateModelValue()
  if (mapped[0]) {
    emit('upload-success', {
      url: mapped[0].url,
      name: mapped[0].name,
      size: items[0]?.size
    })
  }
}

// 文件上传之前判断
const beforeUpload: UploadProps['beforeUpload'] = (file: UploadRawFile) => {
  if (fileList.value.length > props.limit) {
    message.error(`上传文件数量不能超过${props.limit}个!`)
    return false
  }
  let fileExtension = ''
  if (file.name.lastIndexOf('.') > -1) {
    fileExtension = file.name.slice(file.name.lastIndexOf('.') + 1)
  }
  const isImg = props.fileType.some((type: string) => {
    if (file.type.indexOf(type) > -1) return true
    return !!(fileExtension && fileExtension.indexOf(type) > -1)
  })
  const isLimit = file.size < props.fileSize * 1024 * 1024
  if (!isImg) {
    message.error(`文件格式不正确, 请上传${props.fileType.join('/')}格式!`)
    return false
  }
  if (!isLimit) {
    message.error(`上传文件大小不能超过${props.fileSize}MB!`)
    return false
  }
  message.success('正在上传文件，请稍候...')
  // 只有在验证通过后才增加计数器
  uploadNumber.value++
  return true
}
// 文件上传成功
const handleFileSuccess: UploadProps['onSuccess'] = (res: any, uploadFile): void => {
  message.success('上传成功')
  const response = res as { data: string }
  // 删除自身
  const index = fileList.value.findIndex(
    (item) => (item.response as { data?: string } | undefined)?.data === response.data
  )
  fileList.value.splice(index, 1)
  // ⚠️ name 必须取「用户本地原始文件名」，不能取 response.data。
  //    `response.data` 是文件访问 URL（形如 .../get/%E4%BA%91%E8%9F%B9.pdf），
  //    直接拿来当 name 会把中文文件名显示成一大串「%E4%BA%91...」百分号编码。
  uploadList.value.push({
    name: resolveFileName(uploadFile.name, response.data),
    url: response.data
  })
  if (uploadList.value.length == uploadNumber.value) {
    fileList.value.push(...uploadList.value)
    uploadList.value = []
    uploadNumber.value = 0
    emitUpdateModelValue()
    emit('upload-success', {
      url: response.data,
      name: uploadFile.name,
      size: uploadFile.raw?.size ?? uploadFile.size
    })
  }
}
// 文件数超出提示
const handleExceed: UploadProps['onExceed'] = (files): void => {
  if (props.replaceOnExceed && props.limit === 1 && files.length) {
    uploadRef.value?.clearFiles()
    fileList.value = []
    uploadList.value = []
    uploadNumber.value = 0
    const file = files[0] as UploadRawFile
    file.uid = genFileId()
    uploadRef.value?.handleStart(file)
    if (props.autoUpload) uploadRef.value?.submit()
    return
  }
  message.error(`上传文件数量不能超过${props.limit}个!`)
}
// 上传错误提示
const excelUploadError: UploadProps['onError'] = (): void => {
  message.error('导入数据失败，请您重新上传！')
  // 上传失败时减少计数器，避免后续上传被阻塞
  uploadNumber.value = Math.max(0, uploadNumber.value - 1)
}
// 删除上传文件
const handleRemove = (file: UploadFile) => {
  const index = fileList.value.map((f) => f.name).indexOf(file.name)
  if (index > -1) {
    fileList.value.splice(index, 1)
    emitUpdateModelValue()
  }
}
const handlePreview: UploadProps['onPreview'] = (uploadFile) => {
  console.log(uploadFile)
}

// 监听模型绑定值变动
watch(
  () => props.modelValue,
  (val: string | string[]) => {
    if (!val) {
      fileList.value = [] // fix：处理掉缓存，表单重置后上传组件的内容并没有重置
      return
    }

    fileList.value = [] // 保障数据为空
    // 情况1：字符串
    if (isString(val)) {
      fileList.value.push(...val.split(',').map((url) => ({ name: fileNameFromUrl(url), url })))
      return
    }
    // 情况2：数组
    fileList.value.push(...(val as string[]).map((url) => ({ name: fileNameFromUrl(url), url })))
  },
  { immediate: true, deep: true }
)
// 发送文件链接列表更新
const emitUpdateModelValue = () => {
  // 情况1：数组结果
  let result: string | string[] = fileList.value.map((file) => file.url!)
  // 情况2：逗号分隔的字符串
  if (props.limit === 1 || isString(props.modelValue)) {
    result = result.join(',')
  }
  emit('update:modelValue', result)
}
</script>
<style lang="scss" scoped>
.upload-file-uploader {
  margin-bottom: 5px;
}

.file-list-row {
  display: flex;
  align-items: center;
  min-width: 0;
}

.file-list-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-list-action {
  flex-shrink: 0;
}

:deep(.upload-file-list .el-upload-list__item) {
  position: relative;
  margin-bottom: 10px;
  line-height: 2;
  border: 1px solid #e4e7ed;
}

:deep(.el-upload-list__item-file-name) {
  max-width: 250px;
}

:deep(.upload-file-list .ele-upload-list__item-content) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: inherit;
}

:deep(.ele-upload-list__item-content-action .el-link) {
  margin-right: 10px;
}

.file-list-item {
  border: 1px dashed var(--el-border-color-darker);
  border-radius: 8px;
}
</style>
